import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AdminUser, LoginResponse } from '../models/admin-user.model';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root'
})
export class AdminAuthService {
  private currentUserSubject: BehaviorSubject<AdminUser | null>;
  public currentUser$: Observable<AdminUser | null>;
  private readonly TOKEN_KEY = 'admin_token';

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    const storedUser = this.getUserFromToken();
    this.currentUserSubject = new BehaviorSubject<AdminUser | null>(storedUser);
    this.currentUser$ = this.currentUserSubject.asObservable();
  }

  /**
   * Login administrativo
   */
  login(email: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${environment.adminApiUrl}auth/login`,
      { email, password }
    ).pipe(
      tap(response => {
        if (response.success && response.data.token) {
          localStorage.setItem(this.TOKEN_KEY, response.data.token);
          this.currentUserSubject.next(response.data.user);
        }
      }),
      catchError(error => {
        console.error('[AdminAuth] Login error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Registro del primer administrador
   */
  register(data: { email: string; password: string; firstName: string; lastName: string }): Observable<any> {
    return this.http.post<any>(
      `${environment.adminApiUrl}auth/register`,
      data
    ).pipe(
      catchError(error => {
        console.error('[AdminAuth] Register error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Verificar email con código de 4 dígitos
   */
  verifyEmail(email: string, code: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${environment.adminApiUrl}auth/verify-email`,
      { email, code }
    ).pipe(
      tap(response => {
        if (response.success && response.data.token) {
          localStorage.setItem(this.TOKEN_KEY, response.data.token);
          this.currentUserSubject.next(response.data.user);
        }
      }),
      catchError(error => {
        console.error('[AdminAuth] Verify email error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Reenviar código de verificación
   */
  resendVerification(email: string): Observable<any> {
    return this.http.post<any>(
      `${environment.adminApiUrl}auth/resend-verification`,
      { email }
    ).pipe(
      catchError(error => {
        console.error('[AdminAuth] Resend verification error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Solicitar recuperación de contraseña
   */
  forgotPassword(email: string): Observable<any> {
    return this.http.post<any>(
      `${environment.adminApiUrl}auth/forgot-password`,
      { email }
    ).pipe(
      catchError(error => {
        console.error('[AdminAuth] Forgot password error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Restablecer contraseña con código
   */
  resetPassword(email: string, code: string, newPassword: string): Observable<any> {
    return this.http.post<any>(
      `${environment.adminApiUrl}auth/reset-password`,
      { email, code, newPassword }
    ).pipe(
      catchError(error => {
        console.error('[AdminAuth] Reset password error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Logout
   */
  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    this.currentUserSubject.next(null);
    this.router.navigate(['/login']);
  }

  /**
   * Obtener token almacenado
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Verificar si el usuario está autenticado
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    try {
      const decoded: any = jwtDecode(token);
      // Verificar si el token ha expirado
      return decoded.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  /**
   * Obtener usuario actual
   */
  get currentUserValue(): AdminUser | null {
    return this.currentUserSubject.value;
  }

  /**
   * Verificar si tiene un rol específico
   */
  hasRole(role: string): boolean {
    const user = this.currentUserSubject.value;
    return user?.roles?.includes(role) || false;
  }

  /**
   * Verificar si tiene un permiso específico
   */
  hasPermission(permission: string): boolean {
    const user = this.currentUserSubject.value;
    return user?.permissions?.includes(permission) || false;
  }

  /**
   * Verificar si es Super Admin
   */
  isSuperAdmin(): boolean {
    return this.hasRole('SAAS_SUPER_ADMIN');
  }

  /**
   * Obtener información del usuario desde el token
   */
  private getUserFromToken(): AdminUser | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      const decoded: any = jwtDecode(token);
      
      // Verificar si el token ha expirado
      if (decoded.exp * 1000 <= Date.now()) {
        this.logout();
        return null;
      }

      return {
        id: decoded.id,
        email: decoded.email,
        displayName: decoded.displayName || decoded.email,
        roles: decoded.roles || [],
        permissions: decoded.permissions || [],
        isAdmin: decoded.isAdmin || false
      };
    } catch (error) {
      console.error('[AdminAuth] Error decoding token:', error);
      return null;
    }
  }

  /**
   * Actualizar información del usuario
   */
  refreshUserInfo(): Observable<any> {
    return this.http.get<any>(`${environment.adminApiUrl}auth/me`).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.currentUserSubject.next(response.data);
        }
      }),
      catchError(error => {
        console.error('[AdminAuth] Error refreshing user info:', error);
        if (error.status === 401) {
          this.logout();
        }
        return throwError(() => error);
      })
    );
  }
}
